import { assertNever } from '@/lib/typescript/assertNever'
import { Button } from '@/ui/buttons/button/Button.client'
import type { TransitionStartFunction } from 'react'
import type { Actions, CancelActionProps, CustomActionProps } from '../drawer.types'

type CustomActionButtonProps = CustomActionProps & {
  close: () => void
  isPending: boolean
  startTransition: TransitionStartFunction
}

export function CustomActionButton({
  type,
  icon,
  action,
  children,
  close,
  isPending,
  startTransition,
  variant,
  intent,
  isDisabled = false,
}: CustomActionButtonProps) {
  function handleOnPress() {
    startTransition(async () => {
      const shouldClose = await action?.()

      if (shouldClose !== false) {
        close()
      }
    })
  }

  return (
    <Button
      key={type}
      variant={variant}
      size="lg"
      iconLeft={icon}
      isLoading={isPending}
      isDisabled={isPending || isDisabled}
      type="button"
      onPress={handleOnPress}
      className="grow"
      intent={intent}
    >
      {children}
    </Button>
  )
}

type CancelActionButtonProps = CancelActionProps & { isPending: boolean }

export function CancelActionButton({
  type,
  icon,
  children,
  isDisabled: isActionDisabled = false,
  isPending,
  intent,
}: CancelActionButtonProps) {
  return (
    <Button
      key={type}
      variant="secondary"
      size="lg"
      iconLeft={icon}
      isDisabled={isPending || isActionDisabled}
      type="button"
      slot="close"
      className="order-first grow"
      intent={intent}
    >
      {children}
    </Button>
  )
}

type DrawerActionsProps = {
  actions: Actions
  isPending: boolean
  close: () => void
  startTransition: TransitionStartFunction
}

export function DrawerActions({ actions, isPending, close, startTransition }: DrawerActionsProps) {
  const drawerActions = [...actions.custom, actions.cancel, actions.submit].filter(
    (action) => !!action,
  )

  if (!drawerActions.length) {
    return null
  }

  return (
    <div
      className="flex flex-col gap-2"
      data-testid="drawer/actions"
      onClick={(e) => e.stopPropagation()}
    >
      {drawerActions.map((props) => {
        const { icon, children, intent } = props

        switch (props.type) {
          case 'submit':
            return (
              <Button
                key={props.type}
                variant="primary"
                size="lg"
                iconLeft={icon}
                isLoading={isPending || props.form.formState.isSubmitting}
                isDisabled={isPending || props.form.formState.isSubmitting}
                type="submit"
                className="grow"
                intent={intent}
                aria-label={props.ariaLabel}
              >
                {children}
              </Button>
            )
          case 'cancel':
            return (
              <CancelActionButton
                key={props.type}
                type={props.type}
                icon={icon}
                isPending={isPending}
                isDisabled={props.isDisabled}
                intent={intent}
              >
                {children}
              </CancelActionButton>
            )
          case 'custom':
            return (
              <CustomActionButton
                handle={props.handle}
                key={props.handle}
                type={props.type}
                icon={icon}
                action={props.action}
                close={close}
                variant={props.variant || 'primary'}
                isPending={isPending}
                isDisabled={props.isDisabled}
                startTransition={startTransition}
                intent={intent}
              >
                {children}
              </CustomActionButton>
            )
          default:
            assertNever(props)
        }
      })}
    </div>
  )
}
